import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Table,
  Button,
  Form,
  Input,
  Space,
  Typography,
  message,
} from 'antd';
import {
  PrefixFormItem,
  mobileForApi,
  isMobileLengthValid,
  MOBILE_MAX_LEN,
  mobileFieldInputPlain,
} from '../constants/CountryCodes';
import {
  listTeamMembers,
  addTeamMembers,
  removeTeamMembers,
} from '../api/teamClient';

function statusSubline(row) {
  const tags = Array.isArray(row.Tag) ? row.Tag : [];
  if (tags.some((t) => t && t.removed === true)) {
    return null;
  }
  const resigned = [...tags]
    .reverse()
    .find((t) => t && t.status === 'resigned');
  if (!resigned) {
    return null;
  }
  return (
    <Typography.Text
      type="secondary"
      style={{ fontSize: 12, display: 'block', marginTop: 2 }}
    >
      Resigned
      {resigned.Date
        ? ` · ${new Date(resigned.Date).toLocaleString()}`
        : ''}
    </Typography.Text>
  );
}

export default function TeamManagementModal({
  open,
  onCancel,
  token,
}) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const refresh = useCallback(async () => {
    if (!token || !open) return;
    setLoading(true);
    try {
      const data = await listTeamMembers(token);
      setMembers(Array.isArray(data.members) ? data.members : []);
    } catch (e) {
      message.error(
        e.response?.data?.message || e.message || 'Could not load team'
      );
    } finally {
      setLoading(false);
    }
  }, [token, open]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const onAddFinish = async (values) => {
    const userName = mobileForApi(values.prefix, values.mobile);
    setSubmitting(true);
    try {
      await addTeamMembers(token, {
        firstName: values.firstName,
        lastName: values.lastName,
        userName,
        password: values.password,
      });
      message.success('Team member added.');
      setAddOpen(false);
      form.resetFields();
      refresh();
    } catch (e) {
      message.error(
        e.response?.data?.message || e.message || 'Could not add member'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (pk) => {
    try {
      await removeTeamMembers(token, pk);
      message.success('Removed.');
      refresh();
    } catch (e) {
      message.error(
        e.response?.data?.message || e.message || 'Could not remove'
      );
    }
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, row) => (
        <div>
          <div>
            {`${row.FirstName || ''} ${row.LastName || ''}`.trim() || '—'}
          </div>
          {statusSubline(row)}
        </div>
      ),
    },
    {
      title: 'Mobile',
      dataIndex: 'Mobile',
      key: 'Mobile',
      ellipsis: true,
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_, row) => (
        <Button danger size="small" type="link" onClick={() => remove(row.PK)}>
          Remove
        </Button>
      ),
    },
  ];

  return (
    <>
      <Modal
        title="Team Management"
        open={open}
        onCancel={onCancel}
        footer={null}
        width={520}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Button type="primary" onClick={() => setAddOpen(true)}>
            + Add team member
          </Button>
          <Table
            rowKey="PK"
            loading={loading}
            dataSource={members}
            columns={columns}
            pagination={false}
            size="small"
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Team members sign in with Role &quot;Team member&quot; using the mobile
            and password you set here.
          </Typography.Text>
        </Space>
      </Modal>

      <Modal
        title="Add team member"
        open={addOpen}
        onCancel={() => {
          setAddOpen(false);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
        width={420}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onAddFinish}
          initialValues={{ prefix: '+91' }}
        >
          <Form.Item
            name="firstName"
            label="First name"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="lastName"
            label="Last name"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Mobile" required>
            <Space.Compact style={{ width: '100%' }}>
              <PrefixFormItem />
              <Form.Item
                name="mobile"
                noStyle
                rules={[
                  { required: true, message: 'Enter mobile number' },
                  {
                    validator: (_, value) => {
                      if (!value || !isMobileLengthValid(value)) {
                        return Promise.reject(
                          new Error('Enter 6–14 digits')
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  maxLength={MOBILE_MAX_LEN}
                  placeholder="Mobile number"
                  style={{ ...mobileFieldInputPlain, flex: 1, minWidth: 0 }}
                  onChange={(e) => {
                    const v = e.target.value
                      .replace(/\D/g, '')
                      .slice(0, MOBILE_MAX_LEN);
                    form.setFieldsValue({ mobile: v });
                  }}
                />
              </Form.Item>
            </Space.Compact>
          </Form.Item>

          <Form.Item
            name="password"
            label="Set password"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting} block>
              Create team member
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
